import { useEffect, useState } from "react";
import { ChevronRight, ChevronDown, GitBranch, User, Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import { STATUS_STYLE, STATUS_LABEL, inr } from "@/lib/branch";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—");

const BranchNode = ({ b, depth, List }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen(!open)} disabled={!b.direct_referrals} style={{ marginLeft: depth * 20 }}
        className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-[#FEF2F2] text-left disabled:cursor-default" data-testid={`tree-branch-${b.referral_code}`}>
        {b.direct_referrals ? (open ? <ChevronDown className="w-4 h-4 text-[#B91C1C]" /> : <ChevronRight className="w-4 h-4 text-[#B91C1C]" />) : <span className="w-4" />}
        <GitBranch className="w-3.5 h-3.5 text-[#DC2626]" />
        <span className="text-xs font-semibold text-[#7F1D1D]">{b.referral_code}</span>
        <span className="text-xs text-[#64748B]">{b.name} · {b.branch_code}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_STYLE[b.status]}`}>{STATUS_LABEL[b.status]}</span>
        <span className="text-[11px] text-[#94A3B8]">{b.direct_referrals} direct</span>
      </button>
      {open && <List code={b.referral_code} depth={depth + 1} />}
    </div>
  );
};

const MemberNode = ({ m, depth, List }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen(!open)} style={{ marginLeft: depth * 20 }}
        className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 py-2 px-3 rounded-xl bg-white border border-[#F1F5F9] hover:border-[#FECACA] text-left transition-colors" data-testid={`tree-member-${m.user_code}`}>
        {open ? <ChevronDown className="w-4 h-4 text-[#B91C1C]" /> : <ChevronRight className="w-4 h-4 text-[#B91C1C]" />}
        <User className="w-4 h-4 text-[#7F1D1D]" />
        <span className="text-sm font-semibold text-[#7F1D1D]">{m.name}</span>
        <span className="text-xs font-numbers text-[#64748B]">{m.user_code}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${m.is_active ? STATUS_STYLE.active : STATUS_STYLE.pending_payment}`}>{m.is_active ? "Active" : "Pending"}</span>
        <span className="text-[11px] text-[#94A3B8]">Joined {fmt(m.joined_at)} · {m.direct_referrals} direct · team {m.team_size} · sales {inr(m.verified_sales)} · {m.branches.length} branch{m.branches.length === 1 ? "" : "es"}</span>
      </button>
      {open && <div className="mt-1 space-y-0.5">{m.branches.map((b) => <BranchNode key={b.id} b={b} depth={depth + 1} List={List} />)}</div>}
    </div>
  );
};

export const MemberList = ({ code, depth = 0, filters = {} }) => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState("");
  const fkey = JSON.stringify(filters);

  useEffect(() => { setPage(1); }, [fkey]);
  useEffect(() => {
    setData(null);
    api.get(`/network/branch/${code}/members`, { params: { page, ...filters } })
      .then(({ data }) => setData(data)).catch((e) => setErr(e?.response?.data?.detail || "Could not load"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, page, fkey]);

  if (err) return <div className="text-xs text-red-600 py-2" style={{ marginLeft: depth * 20 }}>{err}</div>;
  if (!data) return <div className="py-2 flex items-center gap-2 text-xs text-[#94A3B8]" style={{ marginLeft: depth * 20 }}><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>;
  if (!data.members.length) return <div className="text-xs text-[#94A3B8] py-2" style={{ marginLeft: depth * 20 }} data-testid={`tree-empty-${code}`}>No members joined through {code} yet.</div>;
  return (
    <div className="space-y-1.5 mt-1">
      {data.members.map((m) => <MemberNode key={m.id} m={m} depth={depth} List={MemberList} />)}
      {data.pages > 1 && (
        <div className="flex items-center gap-2 text-xs" style={{ marginLeft: depth * 20 }}>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 rounded border disabled:opacity-40">Prev</button>
          <span>Page {page} / {data.pages} · {data.total} members</span>
          <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="px-2 py-1 rounded border disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
};

export const BranchTree = ({ code }) => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [applied, setApplied] = useState({});
  return (
    <div className="space-y-3" data-testid={`branch-tree-${code}`}>
      <form onSubmit={(e) => { e.preventDefault(); setApplied({ q, status, date_from: from }); }} className="flex flex-wrap gap-2">
        <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#94A3B8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search member name / ID" className="input-soft pl-8 py-2 text-sm w-56" data-testid="tree-search-input" /></div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-soft py-2 text-sm w-36" data-testid="tree-status-filter">
          <option value="">All status</option><option value="active">Active</option><option value="pending">Pending</option>
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input-soft py-2 text-sm w-40" data-testid="tree-date-filter" />
        <button className="btn-primary text-sm" data-testid="tree-apply-filter-btn">Apply</button>
      </form>
      <MemberList code={code} filters={applied} />
    </div>
  );
};
